import React, { useMemo, useState } from "react";
import { Search, ExternalLink } from "lucide-react";
import { useTheme as useCustomTheme } from "../theme/useTheme";
import { useScopedDisabled } from "../hooks/useClientReadOnly";

export const CustomTextField = ({
  placeholder,
  value,
  onChange,
  type = "text",
  sx = {},
  className,
  error,
  maxLength,
  autoAddHttps = false,
  validateUrl = false,
  externalError,
  onValidationChange,
  errorMessage,
  isSearch = false,
  onSearchClick,
  setValueOnClickAway = false,
  onInput,
  focusBorderColor,
  isUrlField = false,
  onUrlClick,
  disabled: disabledProp,
  ...props
}) => {
  const scopedDisabled = useScopedDisabled(disabledProp);
  // Searching never mutates data, so search boxes ignore the read-only scope.
  const disabled = isSearch ? Boolean(disabledProp) : scopedDisabled;
  const DEFAULT_URL_MAX_LENGTH = 300;
  const { theme, colors, neutrals } = useCustomTheme();
  const palette = theme?.palette ?? {};
  const borderPalette = palette.border ?? {};
  const textPalette = palette.text ?? {};
  const defaultBorder = borderPalette.input ?? palette.divider ?? "#d9d9d9";
  const hoverBorder = borderPalette.default ?? palette.divider ?? "#bdbdbd";
  // Borde de foco neutro (negro) en vez del color de marca.
  const focusBorder = focusBorderColor ?? neutrals?.black ?? "#000000";
  const errorBorder = "#d32f2f";
  const backgroundColor = disabled
    ? (palette.action?.disabledBackground ?? "#f5f5f5")
    : (palette.background?.default ?? "#ffffff");
  const fontFamily = theme?.typography?.fontFamily ?? "inherit";

  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [internalError, setInternalError] = useState(false);
  const [internalErrorMessage, setInternalErrorMessage] = useState("");
  const [internalValue, setInternalValue] = useState(value || "");

  // Sincronizar valor interno cuando cambia el valor externo
  React.useEffect(() => {
    setInternalValue(value || "");
  }, [value]);

  // Función para determinar si una URL es válida y clickeable
  const isValidClickableUrl = (url) => {
    // Simplemente verificar que no esté vacío
    return url && url.trim().length > 0;
  };

  const handleChange = (e) => {
    let newValue = e.target.value;

    // Validación y filtrado para tipo tel
    if (type === "tel") {
      // Filtrar: solo dígitos, espacios y máximo un + al inicio
      newValue = newValue.replace(/[^+\d\s]/g, "");
      if (newValue.includes("+") && newValue.indexOf("+") > 0) {
        // Mover + al inicio
        newValue = "+" + newValue.replace(/\+/g, "");
      }
      // Solo un +
      const plusCount = (newValue.match(/\+/g) || []).length;
      if (plusCount > 1) {
        newValue = "+" + newValue.replace(/\+/g, "");
      }
    }

    // Crear un evento sintético con el nuevo valor
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: newValue,
      },
    };

    // Si setValueOnClickAway está activado, solo actualizar el estado interno
    if (setValueOnClickAway) {
      setInternalValue(newValue);
    } else {
      onChange(syntheticEvent);
    }

    // Llamar a onInput si existe (para limpiar errores externos mientras se escribe)
    if (onInput) {
      onInput(syntheticEvent);
    }

    if (internalError) {
      setInternalError(false);
      setInternalErrorMessage("");
    }
  };

  // Función para validar email con regex más robusta
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleBlur = (e) => {
    setFocused(false);
    setHovered(false);
    let hasValidationError = false;
    const currentValue = setValueOnClickAway
      ? internalValue
      : (value?.trim?.() ?? value);

    if (type === "email" && currentValue && currentValue.includes("@")) {
      hasValidationError = !isValidEmail(currentValue);
      setInternalError(hasValidationError);
      if (hasValidationError) {
        setInternalErrorMessage("Introduce un correo electrónico válido");
      }
    } else if ((type === "url" || validateUrl) && currentValue) {
      const urlMaxLength = maxLength ?? DEFAULT_URL_MAX_LENGTH;
      hasValidationError = currentValue.length > urlMaxLength;
      setInternalError(hasValidationError);
      if (hasValidationError) {
        setInternalErrorMessage(
          `La URL no puede superar los ${urlMaxLength} caracteres`
        );
      }
    } else if (maxLength && currentValue && currentValue.length > maxLength) {
      hasValidationError = true;
      setInternalError(hasValidationError);
      setInternalErrorMessage("Campo demasiado largo");
    } else {
      setInternalError(false);
      setInternalErrorMessage("");
    }

    // Si setValueOnClickAway está activado, llamar a onChange aquí con un evento sintético
    // Solo si el valor ha cambiado realmente
    if (setValueOnClickAway && !hasValidationError && internalValue !== value) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e?.target,
          value: internalValue,
        },
      };
      onChange(syntheticEvent);
    }

    if (onValidationChange) {
      onValidationChange(!hasValidationError);
    }
  };

  // Handler para click en el botón de URL
  const handleUrlClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (onUrlClick) {
      // Usar callback personalizado si existe
      onUrlClick(value);
    } else {
      // Comportamiento por defecto: abrir en nueva pestaña
      const currentValue = setValueOnClickAway ? internalValue : value;
      if (!currentValue) return;

      // Normalizar URL agregando https:// si no tiene protocolo
      const normalizedUrl = currentValue.trim();
      const urlToOpen = normalizedUrl.match(/^https?:\/\//)
        ? normalizedUrl
        : `https://${normalizedUrl}`;

      window.open(urlToOpen, '_blank', 'noopener,noreferrer');
    }
  };

  const hasError = error || internalError || externalError;
  const displayErrorMessage = errorMessage || internalErrorMessage;

  const borderColor = hasError
    ? errorBorder
    : focused
      ? focusBorder
      : hovered
        ? hoverBorder
        : defaultBorder;

  const boxShadow = focused ? "none" : "none";

  const standardInputStyle = useMemo(
    () => ({
      width: "100%",
      height: "35px",
      padding: "8px 12px",
      fontSize: "14px",
      lineHeight: 1.4,
      fontFamily,
      backgroundColor,
      color: disabled
        ? (textPalette.disabled ?? "#9e9e9e")
        : (textPalette.primary ?? "#1f1f1f"),
      border: `1px solid ${borderColor}`,
      borderRadius: "8px",
      outline: "none",
      transition: "all 0.2s ease",
      boxShadow,
      cursor: disabled ? "not-allowed" : "text",
      opacity: disabled ? 0.8 : 1,
      ...sx,
    }),
    [
      backgroundColor,
      borderColor,
      boxShadow,
      fontFamily,
      sx,
      textPalette.primary,
      textPalette.disabled,
      disabled,
    ]
  );

  const searchWrapperStyle = useMemo(
    () => ({
      width: "100%",
      height: "36px",
      display: "flex",
      alignItems: "center",
      position: "relative", // establishes containing block for absolute icon
      backgroundColor,
      borderRadius: "8px",
      border: `1px solid ${borderColor}`,
      transition: "all 0.2s ease",
      boxShadow,
      cursor: disabled ? "not-allowed" : "text",
      opacity: disabled ? 0.8 : 1,
      ...sx,
    }),
    [backgroundColor, borderColor, boxShadow, sx, disabled]
  );

  // Buscador siempre visible con el mismo look que el de AveronCRMERP:
  // fondo gris suave, borde casi invisible y lupa a la izquierda.
  const searchBorderColor = hasError
    ? errorBorder
    : focused
      ? focusBorder
      : "rgba(229, 231, 235, 0.3)";

  const searchFieldWrapperStyle = useMemo(
    () => ({
      width: "100%",
      height: "32px",
      display: "flex",
      alignItems: "center",
      position: "relative",
      backgroundColor: disabled
        ? (palette.action?.disabledBackground ?? "#f5f5f5")
        : "rgb(245, 247, 248)",
      borderRadius: "6px",
      border: `1px solid ${searchBorderColor}`,
      transition: "border-color 0.2s ease",
      cursor: disabled ? "not-allowed" : "text",
      opacity: disabled ? 0.5 : 1,
      ...sx,
    }),
    [searchBorderColor, sx, disabled, palette.action?.disabledBackground]
  );

  const searchFieldInputStyle = useMemo(
    () => ({
      flex: 1,
      minWidth: 0,
      height: "100%",
      padding: "6px 12px",
      paddingLeft: "36px", // reserve space for the leading icon
      border: "none",
      outline: "none",
      fontSize: "14px",
      lineHeight: 1.4,
      fontFamily,
      backgroundColor: "transparent",
      color: textPalette.primary ?? "#1f1f1f",
      cursor: disabled ? "not-allowed" : "text",
    }),
    [fontFamily, textPalette.primary, disabled]
  );

  const searchInputStyle = useMemo(
    () => ({
      flex: 1,
      height: "100%",
      padding: "8px 12px",
      paddingRight: "40px", // reserve space for icon so text never overlaps
      border: "none",
      outline: "none",
      fontSize: "14px",
      lineHeight: 1.4,
      fontFamily,
      backgroundColor: "transparent",
      color: disabled
        ? (textPalette.disabled ?? "#9e9e9e")
        : (textPalette.primary ?? "#1f1f1f"),
      cursor: disabled ? "not-allowed" : "text",
    }),
    [fontFamily, textPalette.primary, textPalette.disabled, disabled]
  );

  const searchActionStyle = useMemo(
    () => ({
      cursor: onSearchClick ? "pointer" : "default",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      left: "2px",
      top: 0,
      bottom: 0,
      width: "32px",
      borderRadius: "4px",
      transition: "background-color 0.2s ease",
      pointerEvents: onSearchClick ? "auto" : "none",
    }),
    [onSearchClick]
  );

  const commonInputProps = {
    type: type === "url" ? "text" : type,
    placeholder,
    value: setValueOnClickAway ? internalValue : value,
    onChange: handleChange,
    maxLength,
    disabled,
    onFocus: () => {
      if (!disabled) {
        setFocused(true);
        setHovered(false);
      }
    },
    className,
    ...props,
    // onBlur must come AFTER ...props to ensure internal focus management always runs
    onBlur: (e) => {
      handleBlur(e);
      // Call external onBlur if provided
      if (props.onBlur) {
        props.onBlur(e);
      }
    },
  };

  if (isSearch) {
    return (
      <>
        <div style={searchFieldWrapperStyle}>
          <input {...commonInputProps} style={searchFieldInputStyle} />
          <div
            onClick={onSearchClick}
            style={searchActionStyle}
            onMouseEnter={(event) => {
              if (onSearchClick) {
                event.currentTarget.style.backgroundColor =
                  palette.action?.hover ?? "rgba(0, 0, 0, 0.04)";
              }
            }}
            onMouseLeave={(event) => {
              if (onSearchClick) {
                event.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            <Search
              size={16}
              color={textPalette.muted ?? textPalette.secondary ?? "#6b6b6b"}
              style={{
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
        {hasError && displayErrorMessage && (
          <span
            style={{
              color: errorBorder,
              fontSize: "0.75rem",
              marginTop: "4px",
              display: "block",
            }}
          >
            {displayErrorMessage}
          </span>
        )}
      </>
    );
  }

  // Renderizado para campos de URL con botón clickeable
  if (isUrlField) {
    const currentValue = setValueOnClickAway ? internalValue : value;
    const showUrlButton = isValidClickableUrl(currentValue);

    return (
      <>
        <div
          style={{
            ...searchWrapperStyle,
            position: 'relative',
          }}
          onMouseEnter={() => {
            if (!focused && !hasError) {
              setHovered(true);
            }
          }}
          onMouseLeave={() => setHovered(false)}
        >
          <input
            {...commonInputProps}
            style={{
              ...searchInputStyle,
              paddingRight: showUrlButton ? "40px" : "12px",
            }}
          />
          {showUrlButton && (
            <div
              onClick={handleUrlClick}
              style={{
                cursor: 'pointer',
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                right: "1px",
                top: "1px",
                bottom: "1px",
                width: "36px",
                borderTopRightRadius: "7px",
                borderBottomRightRadius: "7px",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor =
                  palette.action?.hover ?? "rgba(0, 0, 0, 0.04)";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = "transparent";
              }}
              title="Abrir enlace en nueva pestaña"
            >
              <ExternalLink
                size={16}
                color={colors?.primary?.main ?? "#1976d2"}
                style={{
                  pointerEvents: "none",
                }}
              />
            </div>
          )}
        </div>
        {hasError && displayErrorMessage && (
          <span
            style={{
              color: errorBorder,
              fontSize: "0.75rem",
              marginTop: "4px",
              display: "block",
            }}
          >
            {displayErrorMessage}
          </span>
        )}
      </>
    );
  }

  return (
    <>
      <input
        {...commonInputProps}
        style={standardInputStyle}
        onMouseEnter={(event) => {
          if (!focused && !hasError) {
            setHovered(true);
            event.currentTarget.style.borderColor = hoverBorder;
          }
        }}
        onMouseLeave={(event) => {
          setHovered(false);
          if (!focused) {
            event.currentTarget.style.borderColor = defaultBorder;
          }
        }}
      />
      {hasError && displayErrorMessage && (
        <span
          style={{
            color: errorBorder,
            fontSize: "0.75rem",
            marginTop: "4px",
            display: "block",
          }}
        >
          {displayErrorMessage}
        </span>
      )}
    </>
  );
};
